import React, { useState } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    TextField, 
    Snackbar, 
    Alert, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel,
    ThemeProvider,
    createTheme,
    CssBaseline
} from '@mui/material';

// Define the dark theme - same as Login page
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#1976d2',
        },
        background: {
            default: '#121212',
            paper: '#1e1e1e',
        },
        text: {
            primary: '#ffffff',
        },
    },
});

const Dashboard = ({ userEmail, onLogout }) => {
    const [carNumber, setCarNumber] = useState('');
    const [type, setType] = useState('');
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const timestamp = new Date().toISOString();
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/settings/applyGwSettings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    carNumber,
                    type
                })
            });

            if (!response.ok) {
                if(response.status === 404) {
                    throw new Error('Device/Settings not found');
                }
                throw new Error('Failed to update settings');
            }

            console.log(`[${timestamp}] Settings updated successfully by ${userEmail} - Car: ${carNumber}, Type: ${type}\n`);
            
            setNotification({
                open: true,
                message: 'Settings updated successfully!',
                severity: 'success'
            });
            
            // Clear form
            setCarNumber('');
            setType('');

        } catch (error) {
            console.log(`[${timestamp}] Error: Failed to update settings for user ${userEmail}\n`);
            console.log(`Error details: ${error.message}\n`);
            setNotification({
                open: true,
                message: error.message,
                severity: 'error'
            });
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Box sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.default',
            }}>
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    p: 2,
                    backgroundColor: 'background.paper',
                }}>
                    <Typography variant="h5" sx={{ color: '#e0e0e0' }}>
                        Dashboard
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ mr: 2, color: '#e0e0e0' }}>
                            {userEmail}
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={onLogout}
                            sx={{
                                backgroundColor: 'primary.main',
                                '&:hover': { backgroundColor: '#1565c0' },
                            }}
                        >
                            Logout
                        </Button>
                    </Box>
                </Box>

                {/* Main Content */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <Box 
                        component="form" 
                        onSubmit={handleSubmit} 
                        sx={{
                            width: 300,
                            padding: 4,
                            borderRadius: 2,
                            boxShadow: 3,
                            backgroundColor: 'background.paper',
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h5" sx={{ mb: 3, color: '#e0e0e0' }}>
                            Gateway Settings
                        </Typography>
                        
                        <TextField
                            fullWidth
                            label="Car Number"
                            value={carNumber}
                            onChange={(e) => setCarNumber(e.target.value)}
                            required
                            sx={{ mb: 2 }}
                            InputLabelProps={{ style: { color: '#ffffff' } }}
                            InputProps={{ style: { color: '#ffffff' } }}
                        />

                        <FormControl fullWidth sx={{ mb: 3, textAlign: 'left' }}>
                            <InputLabel sx={{ color: '#ffffff' }}>Type</InputLabel>
                            <Select
                                value={type}
                                label="Type"
                                onChange={(e) => setType(e.target.value)}
                                required
                                sx={{ color: '#ffffff', textAlign: 'left' }}
                            >
                                <MenuItem value="preload">Preload</MenuItem>
                                <MenuItem value="onroad">Onroad</MenuItem>
                                <MenuItem value="standard">Default</MenuItem>
                            </Select>
                        </FormControl>

                        <Button 
                            type="submit"
                            variant="contained" 
                            fullWidth
                            sx={{
                                backgroundColor: 'primary.main',
                                '&:hover': { backgroundColor: '#1565c0' },
                            }}
                        >
                            Apply Settings
                        </Button>
                    </Box>
                </Box>

                <Snackbar 
                    open={notification.open} 
                    autoHideDuration={6000} 
                    onClose={handleCloseNotification}
                >
                    <Alert 
                        onClose={handleCloseNotification} 
                        severity={notification.severity}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default Dashboard;